"use client";

import { useEffect, useRef, useState } from "react";
import {
  Cloud,
  Cpu,
  Globe,
  Lock,
  Activity,
  CheckCircle2,
  FileVideo,
  Folder
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useTheme } from "@/hooks/useTheme";
import { useUploadProgressStore } from "@/lib/upload-progress";

const TICK_MS = 80; // ~12.5 Hz; smooth to the eye, half the work of 50 ms

export function HeroMockup() {
  const { isDark } = useTheme();
  const [pingMs] = useState(34);
  const rootRef = useRef<HTMLDivElement>(null);

  // One rAF loop, owned by the root. Pauses when the mockup scrolls off
  // screen so mobile isn't burning battery ticking progress nobody sees.
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (reduceMotion) {
      useUploadProgressStore.getState().setProgress(42);
      return;
    }

    let p = Math.floor(Math.random() * 18) + 8;
    useUploadProgressStore.getState().setProgress(p);

    let raf = 0;
    let last = performance.now();
    let running = true;

    const tick = (now: number) => {
      if (!running) return;
      if (now - last >= TICK_MS) {
        last = now;
        p += 0.45;
        if (p >= 100) p = Math.floor(Math.random() * 18) + 5;
        useUploadProgressStore.getState().setProgress(Math.min(100, Math.floor(p)));
      }
      raf = requestAnimationFrame(tick);
    };

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (!running) {
            running = true;
            raf = requestAnimationFrame(tick);
          }
          useUploadProgressStore.getState().setActive(true);
        } else {
          running = false;
          cancelAnimationFrame(raf);
          useUploadProgressStore.getState().setActive(false);
        }
      },
      { threshold: 0.05 }
    );
    io.observe(root);
    // kick off — assume visible at mount
    raf = requestAnimationFrame(tick);

    return () => {
      running = false;
      cancelAnimationFrame(raf);
      io.disconnect();
    };
  }, []);

  return (
    <div ref={rootRef} className="relative w-full max-w-[560px] mx-auto">
      {/* Glow — static, no filter animation.
          On mobile we shrink + soften it (see globals.css @media override). */}
      <div
        className="pointer-events-none absolute -inset-10 -z-10 opacity-70 blur-3xl hero-glow"
        style={{
          background:
            "radial-gradient(circle, rgba(124,58,237,0.45), transparent 60%), radial-gradient(circle at 70% 70%, rgba(34,211,238,0.35), transparent 60%)"
        }}
      />

      {/* Main card — paint containment so the live progress bar
          updates don't invalidate the whole hero area. */}
      <div
        className={cn(
          "relative rounded-3xl overflow-hidden z-10 [contain:layout_paint]",
          isDark
            ? "bg-[#0E0E14]/90 border border-white/10"
            : "bg-white/90 border border-stone-200/80 shadow-elevated"
        )}
      >
        {/* Window chrome */}
        <div
          className={cn(
            "flex items-center gap-2 px-4 py-3 border-b",
            isDark ? "border-white/5" : "border-stone-200/80"
          )}
        >
          <div className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-rose-400/80" />
            <span className="h-2.5 w-2.5 rounded-full bg-amber-400/80" />
            <span className="h-2.5 w-2.5 rounded-full bg-emerald-400/80" />
          </div>
          <div className="flex-1 mx-3">
            <div
              className={cn(
                "mx-auto max-w-[200px] rounded-md px-3 py-1 text-[10px] text-center font-mono",
                isDark ? "bg-white/5 text-white/60" : "bg-stone-100 text-stone-500"
              )}
            >
              zset://transfer/ab12-fx04
            </div>
          </div>
          <div className="flex items-center gap-1.5 text-[10px] text-stone-500 font-mono">
            <Globe className="h-3 w-3" />
            {pingMs}ms
          </div>
        </div>

        {/* Content */}
        <div className="p-5 sm:p-6 space-y-5">
          {/* Top status row — large number subscribes to the store. */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-brand-500 to-brand-700 text-white shadow-glow-sm">
                <Cloud className="h-4 w-4" />
              </span>
              <div>
                <div className="text-sm font-semibold">uploading to studio.nyc</div>
                <div className="text-[11px] text-stone-500 font-mono">
                  via edge-bom-04 · encrypted
                </div>
              </div>
            </div>
            <div className="text-right">
              <LivePercent />
              <div className="text-[10px] uppercase tracking-wider text-stone-500">
                of 1.2 GB
              </div>
            </div>
          </div>

          {/* Main progress bar — its fill is driven by a direct DOM write,
              so React never reconciles this sub-tree per tick. */}
          <MainProgressBar />

          {/* File list */}
          <div
            className={cn(
              "rounded-2xl p-3",
              isDark ? "bg-white/[0.02] border border-white/5" : "bg-stone-50 border border-stone-200/60"
            )}
          >
            <FileRow
              icon={Folder}
              name="project_hero_final"
              meta="0.4 GB · done"
              done
              isDark={isDark}
            />
            <FileRow
              icon={FileVideo}
              name="interview_4k.mov"
              meta="0.6 GB · done"
              done
              isDark={isDark}
            />
            <LiveFileRow isDark={isDark} />
          </div>

          {/* Bottom chips */}
          <div className="flex flex-wrap items-center gap-1.5">
            <Chip icon={Lock}>AES-256</Chip>
            <Chip icon={Cpu}>Multi-part</Chip>
            <Chip icon={CheckCircle2}>Verified</Chip>
            <Chip icon={Activity} accent>
              Resume-ready
            </Chip>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- Live state consumers ---------- */

function LivePercent() {
  // Selector subscription: this component only re-renders when progress
  // changes, and the store dedupes identical values by default.
  const progress = useUploadProgressStore((s) => s.progress);
  return (
    <div className="text-2xl sm:text-3xl font-bold tracking-tight text-gradient tabular-nums">
      {progress}%
    </div>
  );
}

function MainProgressBar() {
  const { isDark } = useTheme();
  const fillRef = useRef<HTMLDivElement>(null);
  const shimmerRef = useRef<HTMLDivElement>(null);

  // Subscribe to the store *outside* React's render path and write
  // straight to the DOM. This is the main perf win — the bar no longer
  // causes a React reconcile per tick.
  useEffect(() => {
    const write = (progress: number) => {
      const scale = progress / 100;
      if (fillRef.current) {
        fillRef.current.style.transform = `scaleX(${scale})`;
      }
      // Mask the shimmer so it only sweeps over the filled portion.
      if (shimmerRef.current) {
        const widthPct = progress;
        shimmerRef.current.style.left = `${Math.max(0, widthPct - 30)}%`;
      }
    };
    write(useUploadProgressStore.getState().progress);
    return useUploadProgressStore.subscribe((state) => write(state.progress));
  }, []);

  return (
    <div
      className={cn(
        "h-3 rounded-full overflow-hidden relative",
        isDark ? "bg-white/5" : "bg-stone-200"
      )}
    >
      {/* transform: scaleX + transform-origin: left is GPU-composited;
          no layout, no paint of the parent, just a cheap transform. */}
      <div
        ref={fillRef}
        className="absolute inset-y-0 left-0 right-0 origin-left rounded-full bg-gradient-to-r from-brand-600 via-brand-700 to-brand-900"
        style={{ transform: "scaleX(0)" }}
      />
      {/* Shimmer sweep — pure CSS, no JS work per frame.
          Hidden on mobile + reduced-motion via globals.css. */}
      <div
        ref={shimmerRef}
        className="absolute inset-y-0 w-24 opacity-50 hero-shimmer motion-reduce:hidden"
        style={{
          background:
            "linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent)",
          left: 0
        }}
        aria-hidden="true"
      />
    </div>
  );
}

function LiveFileRow({ isDark }: { isDark: boolean }) {
  const progress = useUploadProgressStore((s) => s.progress);
  const fillRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const write = (p: number) => {
      if (fillRef.current) {
        fillRef.current.style.transform = `scaleX(${p / 100})`;
      }
    };
    write(useUploadProgressStore.getState().progress);
    return useUploadProgressStore.subscribe((state) => write(state.progress));
  }, []);

  return (
    <FileRow
      icon={Folder}
      name="raw_footage_day_03"
      meta={`${Math.round(progress * 12)} MB / 1.2 GB · live`}
      live
      isDark={isDark}
      fillRef={fillRef}
      progress={progress}
    />
  );
}

/* ---------- Static rows ---------- */

function FileRow({
  icon: Icon,
  name,
  meta,
  done,
  live,
  isDark,
  progress,
  fillRef
}: {
  icon: React.ElementType;
  name: string;
  meta: string;
  done?: boolean;
  live?: boolean;
  isDark: boolean;
  progress?: number;
  fillRef?: React.RefObject<HTMLDivElement>;
}) {
  return (
    <div
      className={cn(
        "flex items-center gap-2.5 px-2 py-1.5 rounded-lg",
        isDark ? "hover:bg-white/[0.02]" : "hover:bg-white"
      )}
    >
      <span
        className={cn(
          "inline-flex h-7 w-7 items-center justify-center rounded-md",
          done
            ? "bg-emerald-500/15 text-emerald-500"
            : "bg-brand-500/15 text-brand-500"
        )}
      >
        <Icon className="h-3.5 w-3.5" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-medium truncate">{name}</div>
        <div className="text-[10px] text-stone-500">{meta}</div>
        {live && (
          <div
            className={cn(
              "mt-1.5 h-1 rounded-full overflow-hidden",
              isDark ? "bg-white/5" : "bg-stone-200"
            )}
          >
            <div
              ref={fillRef}
              className="h-full origin-left rounded-full bg-gradient-to-r from-brand-600 to-brand-900"
              style={{ transform: "scaleX(0)" }}
            />
          </div>
        )}
      </div>
      {done && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />}
      {live && (
        <span className="text-[10px] font-semibold text-brand-500 tabular-nums">
          {progress}%
        </span>
      )}
    </div>
  );
}

function Chip({
  children,
  icon: Icon,
  accent
}: {
  children: React.ReactNode;
  icon: React.ElementType;
  accent?: boolean;
}) {
  const { isDark } = useTheme();
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-medium",
        accent
          ? "bg-gradient-to-r from-brand-700/15 to-brand-900/15 text-brand-700 dark:text-brand-200 border border-brand-700/20"
          : isDark
          ? "bg-white/[0.04] text-white/70 border border-white/5"
          : "bg-white text-stone-700 border border-stone-200"
      )}
    >
      <Icon className="h-3 w-3" />
      {children}
    </span>
  );
}
